import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import { useUnreadCount } from "../../features/messages/hooks/useUnreadCount";
import { usePortalLinkBuilder } from "../../features/portal/hooks/usePortalLink";
import { selectPortalMe, selectPortalTargetMemberId } from "../../features/portal/portal-slice";
import { ROUTES } from "../../routes";

interface PortalSection {
  route: string;
  labelKey: string;
  /** Prefix match for sections with sub-routes (a Messages thread). */
  nested?: boolean;
}

/**
 * The portal's six sections as text links, the active one underlined; the row scrolls sideways on a
 * phone. Messages carries the unread count; Library is left out when the school switched it off.
 * Every link keeps the shown ward.
 */
const PortalNav = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const me = useAppSelector(selectPortalMe);
  const memberId = useAppSelector(selectPortalTargetMemberId);
  const unread = useUnreadCount(memberId ? { kind: "portal", memberId } : null);
  const link = usePortalLinkBuilder();

  const sections: PortalSection[] = [
    { route: ROUTES.PORTAL, labelKey: "portal.nav.next" },
    { route: ROUTES.PORTAL_MONEY, labelKey: "portal.nav.money" },
    ...(me?.features.materials ? [{ route: ROUTES.PORTAL_LIBRARY, labelKey: "portal.nav.library" }] : []),
    { route: ROUTES.PORTAL_PROGRESS, labelKey: "portal.nav.progress" },
    { route: ROUTES.PORTAL_MESSAGES, labelKey: "portal.nav.messages", nested: true },
    { route: ROUTES.PORTAL_ME, labelKey: "portal.nav.me" },
  ];
  const isActive = (section: PortalSection) =>
    pathname === section.route || (section.nested === true && pathname.startsWith(`${section.route}/`));

  return (
    <nav className="mx-auto max-w-3xl px-4 pb-3" aria-label={t("portal.nav.label")}>
      <div className="-mx-4 flex gap-6 overflow-x-auto whitespace-nowrap px-4">
        {sections.map((section) => (
          <Link
            key={section.route}
            to={link(section.route)}
            aria-current={isActive(section) ? "page" : undefined}
            className={`text-sm text-primary-40 dark:text-primary-90 ${
              isActive(section) ? "underline underline-offset-4" : "hover:underline"
            }`}
          >
            {t(section.labelKey)}
            {section.route === ROUTES.PORTAL_MESSAGES && unread > 0 && (
              <span className="ml-1.5 font-semibold tabular-nums" data-testid="portal-unread-count">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </Link>
        ))}
      </div>
    </nav>
  );
};

export default PortalNav;
